import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';

import type { VirtualizerHandle } from 'virtua';

import {
  classifyActivityRowTransition,
  getActivityViewportPlan,
  getViewportRequest,
} from './activity-feed-viewport';
import type { ActivityNavigationRequest } from './use-activity-navigation';

const EDGE_LOAD_THRESHOLD_ROWS = 5;
const AT_END_TOLERANCE_PX = 2;

interface ActivityFeedViewportOptions {
  /**
   * Whether the end of the scroller is the end of the feed. False when content below the feed
   * shares its scroller, e.g. a composer: a reader at the bottom is then not following new rows.
   */
  endsAtFeedEnd: boolean;
  hasMore: boolean;
  hasPrevious: boolean;
  isLastRowComment: boolean;
  /**
   * Whether the start margin every offset is measured against is known. Placing the viewport before
   * it is lands short of the target by the height of whatever sits above the timeline.
   */
  layoutReady: boolean;
  loadingMore: boolean;
  loadingPrevious: boolean;
  navigationRequest: ActivityNavigationRequest | null;
  navigationScopeId: string;
  /**
   * Whether opening the feed places the reader on the newest row. False for a feed embedded below
   * content worth reading first, which opens where its scroller already sits.
   */
  opensAtNewest: boolean;
  rowIds: string[];
  targetIndex: number | null;
  fetchMore?: () => Promise<unknown>;
  fetchPrevious?: () => Promise<unknown>;
}

interface ActivityFeedViewport {
  onScroll: (offset: number) => void;
  shift: boolean;
  showLatest: () => void;
  virtualizerRef: (handle: VirtualizerHandle | null) => void;
}

interface ViewportSession {
  atEnd: boolean;
  executedCommandKey: string | null;
  fulfilledRequestKey: string | null;
  opensAtNewest: boolean;
  previousRowIds: string[];
  scopeId: string;
}

/**
 * Adapts activity-feed intent to Virtua.
 *
 * All scroll writes live in one layout effect. Everything else may report data, destinations, and
 * reader position, but cannot compete for the scroller.
 */
export function useActivityFeedViewport({
  endsAtFeedEnd,
  fetchMore,
  fetchPrevious,
  hasMore,
  hasPrevious,
  isLastRowComment,
  layoutReady,
  loadingMore,
  loadingPrevious,
  navigationRequest,
  navigationScopeId,
  opensAtNewest,
  rowIds,
  targetIndex,
}: ActivityFeedViewportOptions): ActivityFeedViewport {
  const virtualizerRef = useRef<VirtualizerHandle | null>(null);
  const [virtualizerReady, setVirtualizerReady] = useState(false);
  const sessionRef = useRef<ViewportSession>({
    atEnd: opensAtNewest,
    executedCommandKey: null,
    fulfilledRequestKey: null,
    opensAtNewest,
    previousRowIds: [],
    scopeId: navigationScopeId,
  });
  const [latestRequestId, setLatestRequestId] = useState<number | null>(null);

  if (
    sessionRef.current.scopeId !== navigationScopeId ||
    sessionRef.current.opensAtNewest !== opensAtNewest
  ) {
    sessionRef.current = {
      atEnd: opensAtNewest,
      executedCommandKey: null,
      fulfilledRequestKey: null,
      opensAtNewest,
      previousRowIds: [],
      scopeId: navigationScopeId,
    };
    if (latestRequestId !== null) setLatestRequestId(null);
  }

  const request = useMemo(
    () =>
      getViewportRequest({
        latestRequestId,
        navigationHash: navigationRequest?.hash ?? null,
        navigationScopeId,
        navigationVisit: navigationRequest?.visit ?? null,
        opensAtNewest,
        targetIndex,
      }),
    [latestRequestId, navigationRequest, navigationScopeId, opensAtNewest, targetIndex]
  );
  const transition = classifyActivityRowTransition(sessionRef.current.previousRowIds, rowIds);
  const plan = getActivityViewportPlan({
    atEnd: sessionRef.current.atEnd,
    executedCommandKey: sessionRef.current.executedCommandKey,
    fulfilledRequestKey: sessionRef.current.fulfilledRequestKey,
    isLastRowComment,
    lastRowId: rowIds.at(-1) ?? null,
    request,
    rowCount: rowIds.length,
    transition,
  });

  useLayoutEffect(() => {
    sessionRef.current.previousRowIds = rowIds;
  }, [rowIds]);

  useLayoutEffect(() => {
    if (!virtualizerReady || !layoutReady) return;

    const { command } = plan;
    if (command.type === 'none' || command.key === sessionRef.current.executedCommandKey) return;

    const virtualizer = virtualizerRef.current;
    if (!virtualizer) return;

    sessionRef.current.executedCommandKey = command.key;
    if (command.key === request.key) sessionRef.current.fulfilledRequestKey = request.key;
    if (command.type === 'center') {
      sessionRef.current.atEnd = false;
      virtualizer.scrollToIndex(command.index, { align: 'center', smooth: command.smooth });
      return;
    }

    virtualizer.scrollToIndex(rowIds.length - 1, { align: 'end', smooth: command.smooth });
  }, [layoutReady, plan, request.key, rowIds.length, virtualizerReady]);

  const onScroll = useCallback(
    (offset: number) => {
      const virtualizer = virtualizerRef.current;
      if (!virtualizer) return;

      const distanceToEnd = Math.max(0, virtualizer.scrollSize - virtualizer.viewportSize - offset);
      sessionRef.current.atEnd = endsAtFeedEnd && distanceToEnd <= AT_END_TOLERANCE_PX;

      const firstVisibleIndex = virtualizer.findItemIndex(offset);
      const lastVisibleIndex = virtualizer.findItemIndex(offset + virtualizer.viewportSize);

      if (hasPrevious && !loadingPrevious && firstVisibleIndex <= EDGE_LOAD_THRESHOLD_ROWS)
        void fetchPrevious?.();
      if (hasMore && !loadingMore && lastVisibleIndex + EDGE_LOAD_THRESHOLD_ROWS >= rowIds.length)
        void fetchMore?.();
    },
    [
      endsAtFeedEnd,
      fetchMore,
      fetchPrevious,
      hasMore,
      hasPrevious,
      loadingMore,
      loadingPrevious,
      rowIds.length,
    ]
  );

  const showLatest = useCallback(() => {
    setLatestRequestId(current => (current ?? 0) + 1);
  }, []);

  const setVirtualizerRef = useCallback((handle: VirtualizerHandle | null) => {
    virtualizerRef.current = handle;
    if (handle) setVirtualizerReady(true);
  }, []);

  return { onScroll, shift: plan.shift, showLatest, virtualizerRef: setVirtualizerRef };
}
